const REPO = import.meta.env.VITE_GITHUB_REPO as string
const BRANCH = import.meta.env.VITE_GITHUB_BRANCH as string
const TOKEN = import.meta.env.VITE_GITHUB_TOKEN as string | undefined
const API_BASE = `https://api.github.com/repos/${REPO}/contents`
const TREES_API = `https://api.github.com/repos/${REPO}/git/trees`
const RAW_BASE = `https://raw.githubusercontent.com/${REPO}/${BRANCH}`
const REF = `?ref=${BRANCH}`
const PER_PAGE = 100  // GitHub max for /contents

const CACHE_KEY = 'actuarial_questions_cache'
const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

interface CacheEntry {
  data: string[]   // array of raw Markdown strings
  expiresAt: number
}

function readCache(): string[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry: CacheEntry = JSON.parse(raw)
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return entry.data
  } catch {
    return null
  }
}

function writeCache(data: string[]): void {
  const entry: CacheEntry = { data, expiresAt: Date.now() + CACHE_TTL_MS }
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // localStorage quota exceeded — continue without caching
  }
}

function authHeaders(): Record<string, string> {
  if (TOKEN) {
    return { Authorization: `Bearer ${TOKEN}` }
  }
  return {}
}

const FETCH_TIMEOUT_MS = 10_000

// Wrap fetch with an AbortController so a stalled GitHub request can't hang the app.
async function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

export interface GithubContentItem {
  type: 'file' | 'dir'
  name: string
  path: string
  download_url: string | null
  url: string
}

interface GitTreeItem {
  path: string
  type: 'blob' | 'tree'
  url: string
}

interface GitTreeResponse {
  tree: GitTreeItem[]
  truncated: boolean
}

/**
 * The vault's public repository — where a reader is sent to see the markdown
 * behind a page, a question or a fact-check log.
 *
 * Hard-coded rather than built from `VITE_GITHUB_REPO`: that variable names the
 * *content source* a deployment reads from (which may be a fork or a branch),
 * while this is the project's home, and a deployment that leaves it unset would
 * otherwise hand the reader a link to `undefined`.
 */
export const VAULT_REPO_URL = 'https://github.com/ActuarialNotes/Actuarial-Notes-Wiki'

/** A repo-relative vault path as a link a reader can open. */
export function githubBlobUrl(path: string): string {
  const clean = path.replace(/^\/+/, '')
  const encoded = clean.split('/').map(encodeURIComponent).join('/')
  return `${VAULT_REPO_URL}/blob/main/${encoded}`
}

export function rawGithubUrl(path: string): string {
  const clean = path.replace(/^\/+/, '')
  const encoded = clean.split('/').map(encodeURIComponent).join('/')
  return `https://raw.githubusercontent.com/${REPO}/${BRANCH}/${encoded}`
}

// List any directory in the repo; omit dirPath to list the root.
// Pages through the GitHub Contents API so directories with >30 items aren't truncated.
export async function listRepoContents(dirPath?: string): Promise<GithubContentItem[]> {
  const baseUrl = dirPath ? `${API_BASE}/${dirPath}` : `${API_BASE}`
  const all: GithubContentItem[] = []
  for (let page = 1; ; page++) {
    const url = `${baseUrl}${REF}&per_page=${PER_PAGE}&page=${page}`
    const res = await fetchWithTimeout(url, { headers: authHeaders() })
    if (!res.ok) throw new Error(`GitHub API error ${res.status} for path: ${dirPath ?? '(root)'}`)
    const items = await res.json() as GithubContentItem[]
    if (!Array.isArray(items)) return items as unknown as GithubContentItem[]  // single-file response
    all.push(...items)
    if (items.length < PER_PAGE) break
  }
  return all
}

async function collectMdUrlsViaTree(dirPrefix: string): Promise<string[]> {
  const url = `${TREES_API}/${BRANCH}?recursive=1`
  const res = await fetchWithTimeout(url, { headers: authHeaders() })
  if (!res.ok) throw new Error(`GitHub Trees API error ${res.status}`)
  const data = await res.json() as GitTreeResponse
  if (data.truncated) {
    console.warn('collectMdUrlsViaTree: tree response was truncated — some questions may be missing')
  }
  return data.tree
    .filter(item => item.type === 'blob' && item.path.startsWith(dirPrefix) && item.path.endsWith('.md'))
    .map(item => `${RAW_BASE}/${item.path}`)
}

// Fallback for when the git trees API is unavailable: recursively lists the
// questions/ directory via the Contents API, which hits a separate rate-limit
// bucket from the Trees API.
async function collectMdUrlsViaContents(dirPath: string): Promise<string[]> {
  const items = await listRepoContents(dirPath.replace(/\/$/, ''))
  const urls: string[] = []
  for (const it of items) {
    if (it.type === 'file' && it.name.endsWith('.md')) {
      urls.push(`${RAW_BASE}/${it.path}`)
    } else if (it.type === 'dir') {
      const sub = await collectMdUrlsViaContents(it.path)
      urls.push(...sub)
    }
  }
  return urls
}

// The question bank is bundled at build time (`virtual:questions-content`),
// but as a chunk of its own, fetched the first time anything asks for a
// question — not in the entry chunk, where it was most of the bytes every
// reader downloaded before first paint, whether or not they opened a quiz.
// A failed chunk load (a deploy replaced it mid-visit) is forgotten so the
// next call tries again, and this call falls back to GitHub below.
let bundledQuestions: Promise<string[]> | null = null
function loadBundledQuestions(): Promise<string[]> {
  bundledQuestions ??= import('virtual:questions-content').then(
    mod => mod.default,
    err => {
      bundledQuestions = null
      throw err
    },
  )
  return bundledQuestions
}

export async function fetchAllQuestions(): Promise<string[]> {
  try {
    return await loadBundledQuestions()
  } catch (err) {
    console.warn('fetchAllQuestions: the bundled question bank failed to load, fetching from GitHub:', err)
  }
  const cached = readCache()
  if (cached) return cached

  let urls: string[]
  try {
    urls = await collectMdUrlsViaTree('questions/')
  } catch (treeErr) {
    console.warn('fetchAllQuestions: trees API failed, falling back to contents API:', treeErr)
    urls = await collectMdUrlsViaContents('questions/')
  }
  // Tolerate individual file failures so one transient 5xx doesn't blank the quiz.
  const settled = await Promise.allSettled(
    urls.map(url => fetchWithTimeout(url, { headers: authHeaders() }).then(r => {
      if (!r.ok) throw new Error(`Failed to fetch question file: ${url} (${r.status})`)
      return r.text()
    }))
  )
  const rawFiles: string[] = []
  let failures = 0
  for (const result of settled) {
    if (result.status === 'fulfilled') rawFiles.push(result.value)
    else failures++
  }
  if (failures > 0) console.warn(`fetchAllQuestions: ${failures}/${urls.length} files failed to load`)
  writeCache(rawFiles)
  return rawFiles
}

export function clearQuestionsCache(): void {
  localStorage.removeItem(CACHE_KEY)
}

type ContentLookup = (path: string) => string | undefined
let wikiContentLookup: ContentLookup | null = null
export function setWikiContentLookup(fn: ContentLookup): void { wikiContentLookup = fn }

/**
 * Pages that exist only in the app, registered under a vault-shaped path.
 *
 * `setWikiContentLookup` is a single slot owned by the wiki bundle
 * (`WikiLayout`), so a second feature with pages of its own cannot use it
 * without one of the two winning by module-load order. This registry is the
 * second way in: entries are merged rather than replaced, and consulted ahead
 * of the bundle so a virtual page never has to exist as a file to be readable.
 *
 * Cowork's sample source documents are registered here
 * (`lib/coworkContent.ts`), which is what lets them open in the same popup
 * viewer as a real vault page instead of needing a reader of their own.
 */
const virtualWikiFiles = new Map<string, string>()

export function registerVirtualWikiFiles(files: Record<string, string>): void {
  for (const [path, content] of Object.entries(files)) virtualWikiFiles.set(path, content)
}

const wikiFileMemCache = new Map<string, string>()
const WIKI_FILE_SS_PREFIX = 'wf_'
function ssRead(p: string): string | null {
  try { return sessionStorage.getItem(WIKI_FILE_SS_PREFIX + p) } catch { return null }
}
function ssWrite(p: string, v: string): void {
  try { sessionStorage.setItem(WIKI_FILE_SS_PREFIX + p, v) } catch { /* quota */ }
}

export async function fetchWikiFile(filePath: string): Promise<string> {
  const virtual = virtualWikiFiles.get(filePath)
  if (virtual !== undefined) return virtual
  if (wikiContentLookup) {
    const bundled = wikiContentLookup(filePath)
    if (bundled !== undefined) return bundled
  }
  if (wikiFileMemCache.has(filePath)) return wikiFileMemCache.get(filePath)!
  const ss = ssRead(filePath)
  if (ss !== null) { wikiFileMemCache.set(filePath, ss); return ss }
  const encoded = filePath.split('/').map(encodeURIComponent).join('/')
  const res = await fetchWithTimeout(`${RAW_BASE}/${encoded}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to download wiki file: ${filePath} (${res.status})`)
  const content = await res.text()
  wikiFileMemCache.set(filePath, content)
  ssWrite(filePath, content)
  return content
}
